import React, { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

// Map form-field types and schema-field types to a common "kind" so the
// picker can filter to type-compatible targets. Form types include every
// xavvy designer type plus the inline FormBuilder subset; schema types
// come from the CaseType / CaseTaskType custom-field editor.
const FORM_FIELD_KIND = {
  text: 'text', email: 'text', phone: 'text', url: 'text', password: 'text',
  textarea: 'text', rich_text: 'text', name: 'text', address: 'text',
  number: 'number', integer: 'number', float: 'number', decimal: 'number',
  currency: 'number', percentage: 'number',
  rating: 'number', slider: 'number', range_slider: 'number',
  nps: 'number', opinion_scale: 'number',
  date: 'date', time: 'date', datetime: 'date', date_range: 'date',
  select: 'single_select', searchable_select: 'single_select', combobox: 'single_select',
  radio: 'single_select', radio_cards: 'single_select', image_choice: 'single_select',
  dropdown: 'single_select',
  multiselect: 'multi_select', checkbox_group: 'multi_select',
  checkbox: 'boolean', toggle: 'boolean',
  file: 'file', image: 'file', signature: 'file',
};

const SCHEMA_FIELD_KIND = {
  text: 'text', textarea: 'text',
  number: 'number',
  date: 'date',
  dropdown: 'single_select',
  multi_select: 'multi_select',
  checkbox: 'boolean',
};

const kindOfForm = (type) => FORM_FIELD_KIND[type] || null;
const kindOfSchema = (type) => SCHEMA_FIELD_KIND[type] || null;

/**
 * FieldMappingPicker — searchable picker for mapping a form field to either
 * a case or task custom field (mutually exclusive). Filters to type-compatible
 * catalog entries by default; a toggle exposes all types for cross-type maps.
 *
 * Props:
 *   catalog   — { case_fields, task_fields }. null/undefined → falls back to text inputs.
 *   caseKey   — current case_field_key string
 *   taskKey   — current task_field_key string
 *   fieldType — the form field's type (drives type-compatibility filtering)
 *   fieldName — the form field's name (shown as a placeholder hint)
 *   onChange  — ({ caseKey, taskKey, unmapped }) => void
 */
export default function FieldMappingPicker({ catalog, caseKey, taskKey, onChange, fieldName, fieldType }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const [showAllTypes, setShowAllTypes] = useState(false);
  const [popPos, setPopPos] = useState(null);   // { top, left, width } in viewport coords
  const ref = useRef(null);          // wrapper (contains trigger)
  const popRef = useRef(null);       // portal-rendered popover
  const searchRef = useRef(null);

  // Anchor the popover to the trigger and keep it anchored on scroll/resize.
  // Uses position:fixed so the popover escapes parent overflow clipping.
  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const place = () => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const estHeight = 360;
      // Flip above the trigger if there's not enough room below.
      const below = vh - r.bottom;
      const above = r.top;
      const openUp = below < estHeight && above > below;
      setPopPos({
        top: openUp ? Math.max(8, r.top - estHeight - 2) : r.bottom + 2,
        left: r.left,
        width: r.width,
        maxHeight: openUp ? Math.max(180, above - 12) : Math.max(180, vh - r.bottom - 12),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      const inTrigger = ref.current && ref.current.contains(e.target);
      const inPop = popRef.current && popRef.current.contains(e.target);
      if (!inTrigger && !inPop) { setOpen(false); setQuery(''); }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  useEffect(() => { if (open && searchRef.current) searchRef.current.focus(); }, [open]);

  const caseFields = catalog?.case_fields || [];
  const taskFields = catalog?.task_fields || [];

  const selected = caseKey ? { kind: 'case', key: caseKey } : taskKey ? { kind: 'task', key: taskKey } : null;

  const formKind = kindOfForm(fieldType);
  const typeCompatible = (entry) => {
    if (!formKind) return true;                  // unknown form type → don't filter
    const k = kindOfSchema(entry.type);
    if (!k) return true;                         // unknown schema type → keep
    return k === formKind;
  };
  // Always surface the currently-selected entry even if its type is off,
  // otherwise the picker would show blank when reopening after a switch.
  const keepSelected = (entry, scopeKind) =>
    selected && selected.kind === scopeKind && selected.key === entry.key;

  const filteredCase = useMemo(() => {
    const q = query.trim().toLowerCase();
    const qMatch = (f) => !q || (f.label + ' ' + f.key).toLowerCase().includes(q);
    return caseFields.filter(f => qMatch(f) && (showAllTypes || typeCompatible(f) || keepSelected(f, 'case')));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseFields, query, showAllTypes, fieldType, caseKey, taskKey]);
  const filteredTask = useMemo(() => {
    const q = query.trim().toLowerCase();
    const qMatch = (f) => !q || (f.label + ' ' + f.key).toLowerCase().includes(q);
    return taskFields.filter(f => qMatch(f) && (showAllTypes || typeCompatible(f) || keepSelected(f, 'task')));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskFields, query, showAllTypes, fieldType, caseKey, taskKey]);

  // Count of entries that would reveal under "show all types" — drives the toggle hint.
  const hiddenByType = useMemo(() => {
    if (!formKind) return 0;
    const q = query.trim().toLowerCase();
    const qMatch = (f) => !q || (f.label + ' ' + f.key).toLowerCase().includes(q);
    const total = caseFields.filter(qMatch).length + taskFields.filter(qMatch).length;
    const visible = filteredCase.length + filteredTask.length;
    return Math.max(0, total - visible);
  }, [formKind, query, caseFields, taskFields, filteredCase, filteredTask]);

  const selectedLabel = selected
    ? `${selected.kind === 'case' ? 'Case' : 'Task'} · ${(selected.kind === 'case' ? caseFields : taskFields).find(f => f.key === selected.key)?.label || selected.key}`
    : '';

  const pick = (kind, key) => {
    onChange({ caseKey: kind === 'case' ? key : '', taskKey: kind === 'task' ? key : '', unmapped: false });
    setOpen(false); setQuery('');
  };
  // Explicit unmap — sets no_writeback sentinel so the backend skips the
  // legacy field.name → case field fallback for this field.
  const clear = () => { onChange({ caseKey: '', taskKey: '', unmapped: true }); setOpen(false); setQuery(''); };

  const hasCatalog = !!catalog;

  // Fallback: no catalog available — expose the two text inputs (previous behavior).
  if (!hasCatalog) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <div>
          <label style={labelSm}>Case field (blank = Field ID)</label>
          <input style={inputSm} value={caseKey || ''} placeholder={fieldName || 'case_field_key'}
            onChange={e => onChange({ caseKey: e.target.value.replace(/\s+/g, '_'), taskKey: '', unmapped: false })} />
        </div>
        <div>
          <label style={labelSm}>Task field (blank = skip)</label>
          <input style={inputSm} value={taskKey || ''} placeholder="task_field_key"
            onChange={e => onChange({ caseKey: '', taskKey: e.target.value.replace(/\s+/g, '_'), unmapped: false })} />
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={labelSm}>Writes back to</label>
      <button type="button" onClick={() => setOpen(v => !v)} style={triggerStyle(!!selected)}>
        <span style={{ flex: 1, textAlign: 'left', color: selected ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
          {selected ? selectedLabel : 'Not mapped — click to choose a case or task field'}
        </span>
        {selected && (
          <span onClick={(e) => { e.stopPropagation(); clear(); }}
            style={{ fontSize: 11, color: 'var(--text-tertiary)', marginRight: 6, padding: '0 4px', cursor: 'pointer' }}
            title="Clear mapping">✕</span>
        )}
        <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>▾</span>
      </button>

      {open && popPos && createPortal(
        <div ref={popRef} style={{
          position: 'fixed', zIndex: 9999,
          top: popPos.top, left: popPos.left, width: popPos.width,
          maxHeight: popPos.maxHeight,
          display: 'flex', flexDirection: 'column',
          background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
          borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        }}>
          <input ref={searchRef} value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search fields..." style={searchStyle} />
          {formKind && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 10px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)', fontSize: 10, color: 'var(--text-tertiary)' }}>
              <span>Showing fields compatible with <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{formKind.replace('_', ' ')}</span></span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: showAllTypes ? 'var(--accent-color)' : 'inherit' }}>
                <input type="checkbox" checked={showAllTypes} onChange={e => setShowAllTypes(e.target.checked)} style={{ margin: 0 }} />
                Show all {hiddenByType > 0 ? `(+${hiddenByType})` : ''}
              </label>
            </div>
          )}
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <div style={optionRow} onClick={clear}>
              <span style={{ color: 'var(--text-tertiary)' }}>(not mapped)</span>
            </div>

            {filteredCase.length > 0 && <div style={groupHeader}>Case fields</div>}
            {filteredCase.map(f => {
              const off = formKind && !typeCompatible(f);
              return (
                <div key={`c-${f.key}`}
                  style={{ ...optionRow, opacity: off ? 0.6 : 1, background: selected?.kind === 'case' && selected.key === f.key ? 'var(--bg-tertiary)' : undefined }}
                  onClick={() => pick('case', f.key)}
                  title={off ? `Type mismatch — form field is ${formKind.replace('_', ' ')}` : ''}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {f.label}
                      {f.is_default && <span style={defaultTag} title="Built-in Case field">built-in</span>}
                    </div>
                    <div style={hintText}>{f.key} · {(f.case_types || []).join(', ') || '—'}</div>
                  </div>
                  <span style={{ ...typeTag, background: off ? 'rgba(239,68,68,0.18)' : typeTag.background, color: off ? '#ef4444' : typeTag.color }}>{f.type}</span>
                </div>
              );
            })}

            {filteredTask.length > 0 && <div style={groupHeader}>Task fields</div>}
            {filteredTask.map(f => {
              const off = formKind && !typeCompatible(f);
              return (
                <div key={`t-${f.key}`}
                  style={{ ...optionRow, opacity: off ? 0.6 : 1, background: selected?.kind === 'task' && selected.key === f.key ? 'var(--bg-tertiary)' : undefined }}
                  onClick={() => pick('task', f.key)}
                  title={off ? `Type mismatch — form field is ${formKind.replace('_', ' ')}` : ''}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {f.label}
                      {f.is_default && <span style={defaultTag} title="Built-in Task field">built-in</span>}
                    </div>
                    <div style={hintText}>{f.key} · {(f.scopes || []).join(', ') || '—'}</div>
                  </div>
                  <span style={{ ...typeTag, background: off ? 'rgba(239,68,68,0.18)' : typeTag.background, color: off ? '#ef4444' : typeTag.color }}>{f.type}</span>
                </div>
              );
            })}

            {filteredCase.length === 0 && filteredTask.length === 0 && (
              <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-tertiary)' }}>
                {formKind && !showAllTypes && hiddenByType > 0
                  ? `No ${formKind.replace('_', ' ')}-compatible fields. Toggle "Show all" above to see ${hiddenByType} other field${hiddenByType === 1 ? '' : 's'}.`
                  : 'No matching fields. Define custom fields on a case type or task type first, or type a custom key below.'}
              </div>
            )}
          </div>

          {/* Custom-key escape hatch — some keys may not be declared yet. */}
          <div style={{ borderTop: '1px solid var(--border-color)', padding: '6px 8px' }}>
            {!customMode ? (
              <button type="button" onClick={() => setCustomMode(true)}
                style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: 11, padding: 0 }}>
                + Type a custom key
              </button>
            ) : (
              <CustomKeyForm onCancel={() => setCustomMode(false)} onSubmit={(kind, key) => { setCustomMode(false); pick(kind, key); }} />
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function CustomKeyForm({ onCancel, onSubmit }) {
  const [kind, setKind] = useState('case');
  const [key, setKey] = useState('');
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <select value={kind} onChange={e => setKind(e.target.value)} style={{ ...inputSm, width: 70, fontSize: 11, padding: '3px 4px' }}>
        <option value="case">case</option>
        <option value="task">task</option>
      </select>
      <input value={key} onChange={e => setKey(e.target.value.replace(/\s+/g, '_'))}
        placeholder="custom_key" style={{ ...inputSm, flex: 1, fontSize: 11, padding: '3px 6px' }}
        onKeyDown={e => { if (e.key === 'Enter' && key.trim()) onSubmit(kind, key.trim()); }} />
      <button type="button" onClick={() => key.trim() && onSubmit(kind, key.trim())}
        style={{ background: 'var(--accent-color)', color: 'white', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>Use</button>
      <button type="button" onClick={onCancel}
        style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 11 }}>Cancel</button>
    </div>
  );
}

const labelSm = { display: 'block', fontSize: 11, color: 'var(--text-secondary)', marginBottom: 3 };
const inputSm = { width: '100%', padding: '5px 8px', borderRadius: 4, border: '1px solid var(--border-color)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 12 };
const triggerStyle = (filled) => ({
  width: '100%', padding: '5px 8px', borderRadius: 4,
  border: `1px solid ${filled ? 'var(--accent-color)' : 'var(--border-color)'}`,
  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
  fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
});
const searchStyle = { ...inputSm, border: 'none', borderBottom: '1px solid var(--border-color)', borderRadius: '6px 6px 0 0' };
const groupHeader = { fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 10px 2px' };
const optionRow = { padding: '6px 10px', display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer', fontSize: 12 };
const hintText = { fontSize: 10, color: 'var(--text-tertiary)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const typeTag = { fontSize: 9, color: 'var(--text-tertiary)', background: 'var(--bg-tertiary)', padding: '1px 5px', borderRadius: 3, textTransform: 'uppercase', flexShrink: 0 };
const defaultTag = { fontSize: 9, color: 'var(--accent-color)', background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.35)', padding: '0 5px', borderRadius: 3, textTransform: 'uppercase', letterSpacing: 0.4, flexShrink: 0 };
